// src/lib/generatePDF.ts: standalone, self-contained HTML documents for the
// platform access agreement and its invoice.
//
// These are whole HTML files, not React. They are uploaded to Supabase storage
// and opened in a new tab, where the reader uses the browser's own
// Print → Save as PDF. Everything is inline: no external CSS, fonts or images,
// so a stored document renders identically years from now.
//
// Company details come from COMPANY in ./applications so the bracketed
// placeholders only ever have to be filled in once.
import { COMPANY, ManagerApplication, jurisdictionLabel } from './applications';

// ── Shared helpers ───────────────────────────────────────────────────────────

// Anything originating from an application row is user input. Escape it.
function esc(value: unknown): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// &pound; rather than a literal £: the entity is pure ASCII, so the symbol
// survives whatever encoding the document is later stored, served or opened
// with. A raw £ byte-pair shows up as "Â£" the moment anything reads the file
// as Latin-1.
const money = (n: number) => `&pound;${Number(n || 0).toLocaleString('en-GB')}`;

const longDate = (d: Date | string) =>
  new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

const LOGO = `<svg width="36" height="26" viewBox="0 0 40 28" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M 2 14 A 18 18 0 0 1 38 14" stroke="#5dc9a8" stroke-width="2.5" stroke-linecap="round" fill="none"/>
  <path d="M 6 14 A 14 14 0 0 0 34 14" stroke="#C9A84C" stroke-width="2.5" stroke-linecap="round" fill="none"/>
  <circle cx="20" cy="14" r="3" fill="#C9A84C"/>
</svg>`;

const companyAddressHTML = COMPANY.addressLines.map(l => esc(l)).join('<br>');
const companyAddressInline = COMPANY.addressLines.join(', ');

// A signature captured in the platform, embedded into the signed agreement.
export interface ContractSignature {
  name: string;
  title: string;
  /** PNG data URL from the signature pad. Empty when the signature was typed. */
  imageDataUrl?: string;
  /** The typed name, used when no drawing was made. */
  typedName?: string;
  signedAt: string;
}

export interface ContractData {
  firmName: string;
  contactName: string;
  contactEmail: string;
  jurisdiction: string;
  regulatoryNumber: string;
  contractType: 'standard' | 'emerging';
  annualFee: number;
  /** Funds covered by the annual fee. */
  fundsIncluded: number;
  /** Funds beyond the included allowance, each charged at additionalFundPrice. */
  additionalFunds: number;
  additionalFundPrice: number;
  maxUsers: number;
  specialTerms: string;
  startDate: string;
  invoiceNumber: string;
  signature?: ContractSignature;
}

export interface InvoiceData {
  firmName: string;
  contactName: string;
  contactEmail: string;
  firmAddress: string;
  invoiceNumber: string;
  annualFee: number;
  fundsIncluded: number;
  additionalFunds: number;
  additionalFundPrice: number;
  contractType: 'standard' | 'emerging';
  maxUsers: number;
  dueDate: string;
}

export const contractTotal = (d: { annualFee: number; additionalFunds: number; additionalFundPrice: number }) =>
  d.annualFee + Math.max(0, d.additionalFunds) * d.additionalFundPrice;

const tierLabel = (t: string) => (t === 'emerging' ? 'Emerging Manager' : 'Standard Manager');

// ── Contract ─────────────────────────────────────────────────────────────────

export function generateContractHTML(data: ContractData): string {
  const today = longDate(new Date());
  const total = contractTotal(data);
  const sig = data.signature;

  // The manager's half of the signature block: blank ruled lines before
  // signing, the captured signature afterwards.
  const managerSignature = sig
    ? `<div class="sig-drawn">${
        sig.imageDataUrl
          ? `<img src="${sig.imageDataUrl}" alt="Signature of ${esc(sig.name)}" style="max-height:56px;max-width:100%;">`
          : `<span class="sig-typed">${esc(sig.typedName || sig.name)}</span>`
      }</div>
      <div style="font-weight:600;margin-bottom:4px;">For and on behalf of ${esc(data.firmName)}</div>
      <div class="sig-label">Name: ${esc(sig.name)}</div>
      ${sig.title ? `<div class="sig-label">Title: ${esc(sig.title)}</div>` : ''}
      <div class="sig-label">Date: ${longDate(sig.signedAt)}</div>
      <div class="sig-verified">Digitally signed via the Altveno platform</div>`
    : `<div class="sig-line"></div>
      <div style="font-weight:600;margin-bottom:4px;">For and on behalf of ${esc(data.firmName)}</div>
      <div class="sig-label">Name: ${esc(data.contactName)}</div>
      <div class="sig-label">Date: ____________________________</div>`;

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>Platform Access Agreement, ${esc(data.firmName)}</title>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body {
    font-family: Georgia, 'Times New Roman', serif;
    font-size: 12pt;
    color: #0A0A0A;
    line-height: 1.6;
    padding: 60px;
    max-width: 800px;
    margin: 0 auto;
    background: #FFFFFF;
  }
  .header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 48px;
    padding-bottom: 24px;
    border-bottom: 2px solid #0A1628;
  }
  .logo-section { display: flex; align-items: center; gap: 10px; }
  .logo-text {
    font-family: Georgia, serif;
    font-size: 22pt;
    color: #0A1628;
    font-weight: 400;
  }
  .doc-title {
    font-size: 11pt;
    color: #6B7280;
    text-align: right;
    font-family: system-ui, sans-serif;
  }
  .doc-title strong {
    display: block;
    font-size: 16pt;
    color: #0A1628;
    font-family: Georgia, serif;
    font-weight: 400;
    margin-bottom: 4px;
  }
  h1 {
    font-size: 18pt;
    color: #0A1628;
    margin-bottom: 8px;
    font-weight: 400;
  }
  h2 {
    font-size: 12pt;
    color: #0A1628;
    margin: 24px 0 8px;
    font-weight: 700;
    font-family: system-ui, sans-serif;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  p { margin-bottom: 12px; font-size: 11pt; }
  .parties {
    background: #F5F6F8;
    border-left: 3px solid #C9A84C;
    padding: 16px 20px;
    margin: 24px 0;
    font-family: system-ui, sans-serif;
    font-size: 11pt;
  }
  .parties .label {
    font-size: 9pt;
    color: #6B7280;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 2px;
    font-weight: 700;
  }
  .parties .value { color: #0A1628; font-weight: 600; margin-bottom: 12px; }
  .parties .value:last-child { margin-bottom: 0; }
  .fee-table {
    width: 100%;
    border-collapse: collapse;
    margin: 16px 0;
    font-family: system-ui, sans-serif;
    font-size: 11pt;
  }
  .fee-table th {
    background: #0A1628;
    color: #C9A84C;
    padding: 10px 14px;
    text-align: left;
    font-size: 10pt;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  .fee-table td {
    padding: 10px 14px;
    border-bottom: 1px solid #E8EBF0;
  }
  .fee-table tr:nth-child(even) td { background: #F5F6F8; }
  .signature-block {
    margin-top: 48px;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 40px;
    page-break-inside: avoid;
  }
  .sig-box {
    border-top: 1px solid #0A1628;
    padding-top: 12px;
    font-family: system-ui, sans-serif;
    font-size: 10pt;
  }
  .sig-line {
    height: 60px;
    border-bottom: 1px solid #D0D5DD;
    margin-bottom: 8px;
  }
  .sig-drawn {
    height: 60px;
    border-bottom: 1px solid #D0D5DD;
    margin-bottom: 8px;
    display: flex;
    align-items: flex-end;
    padding-bottom: 2px;
  }
  .sig-typed {
    font-family: 'Segoe Script', 'Brush Script MT', 'Snell Roundhand', cursive;
    font-size: 24pt;
    color: #0A1628;
    line-height: 1.1;
  }
  .sig-label { color: #6B7280; font-size: 9pt; }
  .sig-verified {
    margin-top: 8px;
    font-size: 8.5pt;
    color: #0A7C42;
    font-weight: 700;
    letter-spacing: 0.3px;
  }
  .confidential {
    margin-top: 48px;
    padding-top: 16px;
    border-top: 1px solid #E8EBF0;
    text-align: center;
    font-size: 9pt;
    color: #9CA3AF;
    font-family: system-ui, sans-serif;
  }
  @media print {
    body { padding: 40px; }
    .no-print { display: none; }
  }
</style>
</head>
<body>

<div class="header">
  <div class="logo-section">
    ${LOGO}
    <span class="logo-text">Altveno</span>
  </div>
  <div class="doc-title">
    <strong>Platform Access Agreement</strong>
    Date: ${today}<br>
    Ref: ${esc(data.invoiceNumber)}
  </div>
</div>

<h1>Platform Access Agreement</h1>
<p style="color:#6B7280;font-family:system-ui,sans-serif;font-size:11pt;">
  This agreement is dated ${today} and sets out the terms of access
  to the Altveno alternative investment platform.
</p>

<div class="parties">
  <div class="label">Platform Provider</div>
  <div class="value">${esc(COMPANY.name)} of ${esc(companyAddressInline)}, company number ${esc(COMPANY.companyNumber)} ("Altveno")</div>
  <div class="label">Client</div>
  <div class="value">${esc(data.firmName)} ("the Manager")</div>
  <div class="label">Authorised Contact</div>
  <div class="value">${esc(data.contactName)}, ${esc(data.contactEmail)}</div>
  <div class="label">Regulatory Reference</div>
  <div class="value">${esc(data.jurisdiction)}, ${esc(data.regulatoryNumber)}</div>
</div>

<h2>1. Platform Access</h2>
<p>Altveno grants the Manager access to the Altveno alternative
investment platform ("the Platform") subject to the terms of this
agreement. Access includes all modules available to manager accounts
including AltConnect, Altveno AI, DDQ Manager, Data Room,
ServiceConnect and TalentConnect.</p>

<h2>2. Subscription Fees</h2>
<table class="fee-table">
  <thead>
    <tr>
      <th>Item</th>
      <th>Detail</th>
      <th>Annual Fee</th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <td>Platform Access</td>
      <td>${tierLabel(data.contractType)}, ${data.fundsIncluded} fund${data.fundsIncluded !== 1 ? 's' : ''} included</td>
      <td>${money(data.annualFee)}</td>
    </tr>
    ${data.additionalFunds > 0 ? `
    <tr>
      <td>Additional Funds</td>
      <td>${data.additionalFunds} additional fund${data.additionalFunds !== 1 ? 's' : ''} at ${money(data.additionalFundPrice)} each</td>
      <td>${money(data.additionalFunds * data.additionalFundPrice)}</td>
    </tr>` : ''}
    <tr>
      <td style="font-weight:700;">Total Annual Fee</td>
      <td></td>
      <td style="font-weight:700;">${money(total)}</td>
    </tr>
  </tbody>
</table>
<p style="font-size:10pt;color:#6B7280;font-family:system-ui,sans-serif;">
  Fees are payable annually in advance and are exclusive of VAT where
  applicable. The subscription renews automatically unless cancelled
  with 30 days written notice.
</p>

<h2>3. Authorised Users</h2>
<p>The Manager may grant platform access to up to ${data.maxUsers}
authorised users. Additional users may be added at an agreed rate.
All users must be employees or partners of ${esc(data.firmName)} and
must comply with the platform's acceptable use policy. Accounts are
personal and must not be shared.</p>

<h2>4. Acceptable Use</h2>
<p>The Manager agrees to use the Platform solely for legitimate
alternative investment management and investor relations activities.
The Manager shall not share login credentials, scrape platform data,
or use the Platform in any manner that may harm Altveno or other
platform participants.</p>

<h2>5. Data and Confidentiality</h2>
<p>Each party agrees to keep confidential all non-public information
received from the other party. The Manager retains ownership of the
material it uploads and grants Altveno the licence needed to host and
display it to the audiences the Manager selects. Performance data may
be used in anonymised, aggregated form for platform benchmarking.
Altveno will process personal data in accordance with its Privacy
Policy and applicable data protection legislation including UK GDPR.</p>

<h2>6. Term and Termination</h2>
<p>This agreement commences on ${longDate(data.startDate)} and continues for
an initial term of 12 months. Either party may terminate this
agreement with 30 days written notice. Altveno may suspend or
terminate access immediately for material breach of these terms.
Fees already paid are non-refundable except where required by law.</p>

${data.specialTerms.trim() ? `
<h2>7. Special Terms</h2>
<p style="white-space:pre-wrap;">${esc(data.specialTerms)}</p>` : ''}

<div class="signature-block">
  <div class="sig-box">
    <div class="sig-line"></div>
    <div style="font-weight:600;margin-bottom:4px;">For and on behalf of Altveno</div>
    <div class="sig-label">Name: ____________________________</div>
    <div class="sig-label">Date: ____________________________</div>
  </div>
  <div class="sig-box" id="manager-sig-block">
    ${managerSignature}
  </div>
</div>

<div class="confidential">
  Confidential, Altveno Platform Access Agreement, ${esc(data.firmName)}
  ${sig ? `<br>Digitally signed by ${esc(sig.name)} on ${longDate(sig.signedAt)} via the Altveno platform.` : ''}
</div>

</body>
</html>`;
}

// ── Invoice ──────────────────────────────────────────────────────────────────

export function generateInvoiceHTML(data: InvoiceData): string {
  const today = longDate(new Date());
  const total = contractTotal({
    annualFee: data.annualFee,
    additionalFunds: data.additionalFunds,
    additionalFundPrice: data.additionalFundPrice,
  });

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>Invoice ${esc(data.invoiceNumber)}, ${esc(data.firmName)}</title>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body {
    font-family: system-ui, -apple-system, sans-serif;
    font-size: 11pt;
    color: #0A0A0A;
    padding: 60px;
    max-width: 800px;
    margin: 0 auto;
    background: #FFFFFF;
  }
  .header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 48px;
  }
  .logo-text {
    font-family: Georgia, serif;
    font-size: 22pt;
    color: #0A1628;
  }
  .invoice-label {
    font-size: 28pt;
    font-weight: 700;
    color: #0A1628;
    letter-spacing: -1px;
  }
  .meta-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 32px;
    margin: 32px 0;
    padding: 24px;
    background: #F5F6F8;
    border-radius: 4px;
  }
  .meta-section .label {
    font-size: 9pt;
    color: #6B7280;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-weight: 700;
    margin-bottom: 6px;
  }
  .meta-section .value { color: #0A1628; font-weight: 500; line-height: 1.7; }
  .meta-section .value strong { display: block; font-size: 12pt; }
  table {
    width: 100%;
    border-collapse: collapse;
    margin: 24px 0;
  }
  thead th {
    background: #0A1628;
    color: #C9A84C;
    padding: 12px 16px;
    text-align: left;
    font-size: 10pt;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  thead th:last-child { text-align: right; }
  td {
    padding: 12px 16px;
    border-bottom: 1px solid #E8EBF0;
  }
  td:last-child { text-align: right; }
  .total-row td {
    font-weight: 700;
    font-size: 13pt;
    border-top: 2px solid #0A1628;
    border-bottom: none;
  }
  .payment-box {
    margin-top: 32px;
    padding: 20px 24px;
    border: 1px solid #D0D5DD;
    border-radius: 4px;
    border-left: 3px solid #C9A84C;
    page-break-inside: avoid;
  }
  .payment-box h3 {
    font-size: 10pt;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #6B7280;
    margin-bottom: 12px;
  }
  .payment-row {
    display: flex;
    justify-content: space-between;
    padding: 4px 0;
    font-size: 11pt;
  }
  .payment-label { color: #6B7280; }
  .footer {
    margin-top: 48px;
    padding-top: 16px;
    border-top: 1px solid #E8EBF0;
    text-align: center;
    font-size: 10pt;
    color: #9CA3AF;
    line-height: 1.7;
  }
  @media print {
    body { padding: 40px; }
    .no-print { display: none; }
  }
</style>
</head>
<body>

<div class="header">
  <div>
    <div style="display:flex;align-items:center;gap:10px;margin-bottom:8px;">
      ${LOGO}
      <span class="logo-text">Altveno</span>
    </div>
    <div style="font-size:10pt;color:#6B7280;line-height:1.6;">
      ${esc(COMPANY.name)}<br>
      ${companyAddressHTML}<br>
      ${esc(COMPANY.email)}<br>
      altveno.com
    </div>
  </div>
  <div style="text-align:right;">
    <div class="invoice-label">INVOICE</div>
    <div style="color:#6B7280;margin-top:8px;line-height:1.8;">
      Invoice No: <strong style="color:#0A1628;">${esc(data.invoiceNumber)}</strong><br>
      Date: ${today}<br>
      Due: ${longDate(data.dueDate)}
    </div>
  </div>
</div>

<div class="meta-grid">
  <div class="meta-section">
    <div class="label">Billed To</div>
    <div class="value">
      <strong>${esc(data.firmName)}</strong>
      ${esc(data.contactName)}<br>
      ${esc(data.contactEmail)}${data.firmAddress ? `<br>${esc(data.firmAddress)}` : ''}
    </div>
  </div>
  <div class="meta-section">
    <div class="label">Subscription Type</div>
    <div class="value">
      <strong>${tierLabel(data.contractType)}</strong>
      Annual Platform Subscription<br>
      Valid for 12 months from activation
    </div>
  </div>
</div>

<table>
  <thead>
    <tr>
      <th>Description</th>
      <th>Qty</th>
      <th>Unit Price</th>
      <th>Amount</th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <td>
        <strong>Altveno Platform Access, Annual Subscription</strong><br>
        <span style="color:#6B7280;font-size:10pt;">
          ${tierLabel(data.contractType)} tier,
          ${data.fundsIncluded} fund${data.fundsIncluded !== 1 ? 's' : ''} included, up to ${data.maxUsers} users
        </span>
      </td>
      <td>1</td>
      <td>${money(data.annualFee)}</td>
      <td>${money(data.annualFee)}</td>
    </tr>
    ${data.additionalFunds > 0 ? `
    <tr>
      <td>
        <strong>Additional Fund Access</strong><br>
        <span style="color:#6B7280;font-size:10pt;">
          ${data.additionalFunds} fund${data.additionalFunds !== 1 ? 's' : ''} beyond the included allowance
        </span>
      </td>
      <td>${data.additionalFunds}</td>
      <td>${money(data.additionalFundPrice)}</td>
      <td>${money(data.additionalFunds * data.additionalFundPrice)}</td>
    </tr>` : ''}
    <tr>
      <td style="color:#6B7280;font-size:10pt;">VAT (0%)</td>
      <td></td>
      <td></td>
      <td>&pound;0.00</td>
    </tr>
    <tr class="total-row">
      <td>Total Due</td>
      <td></td>
      <td></td>
      <td>${money(total)}</td>
    </tr>
  </tbody>
</table>

<div class="payment-box">
  <h3>Payment Details</h3>
  <div class="payment-row">
    <span class="payment-label">Bank Name</span>
    <span>${esc(COMPANY.bank.name)}</span>
  </div>
  <div class="payment-row">
    <span class="payment-label">Account Name</span>
    <span>${esc(COMPANY.bank.accountName)}</span>
  </div>
  <div class="payment-row">
    <span class="payment-label">Sort Code</span>
    <span>${esc(COMPANY.bank.sortCode)}</span>
  </div>
  <div class="payment-row">
    <span class="payment-label">Account Number</span>
    <span>${esc(COMPANY.bank.accountNumber)}</span>
  </div>
  <div class="payment-row">
    <span class="payment-label">IBAN</span>
    <span>${esc(COMPANY.bank.iban)}</span>
  </div>
  <div class="payment-row">
    <span class="payment-label">Reference</span>
    <span>${esc(data.invoiceNumber)}</span>
  </div>
  <div style="margin-top:12px;padding-top:12px;border-top:1px solid #E8EBF0;
    font-size:10pt;color:#6B7280;">
    Payment due within 30 days. Please use invoice number as reference.
  </div>
</div>

<div class="footer">
  Thank you for joining Altveno &middot; altveno.com &middot; ${esc(COMPANY.email)}<br>
  ${esc(COMPANY.name)} &middot; Company no. ${esc(COMPANY.companyNumber)} &middot; VAT no. ${esc(COMPANY.vatNumber)}<br>
  ${esc(companyAddressInline)}
</div>

</body>
</html>`;
}

// ── Building document data from an application row ───────────────────────────
//
// Admin and applicant both derive their document data here, so the agreement
// the applicant signs is byte-identical to the one the admin sent.

/** Funds charged on top of the included allowance. */
export const extraFundCount = (app: ManagerApplication) =>
  Math.max(0, (app.fund_count || 1) - (app.funds_included || 1));

export function contractDataFromApp(app: ManagerApplication, signature?: ContractSignature): ContractData {
  return {
    firmName: app.firm_name,
    contactName: app.contact_name,
    contactEmail: app.contact_email,
    jurisdiction: jurisdictionLabel(app.jurisdiction),
    regulatoryNumber: app.regulatory_number,
    contractType: (app.contract_type === 'emerging' ? 'emerging' : 'standard'),
    annualFee: app.contract_price || 0,
    fundsIncluded: app.funds_included || 1,
    additionalFunds: extraFundCount(app),
    additionalFundPrice: app.additional_fund_price || 0,
    maxUsers: app.max_users || 5,
    specialTerms: app.special_terms || '',
    startDate: app.contract_start_date || new Date().toISOString().slice(0, 10),
    invoiceNumber: app.invoice_number || '-',
    signature,
  };
}

export function invoiceDataFromApp(app: ManagerApplication, dueDate: string): InvoiceData {
  return {
    firmName: app.firm_name,
    contactName: app.contact_name,
    contactEmail: app.contact_email,
    firmAddress: [app.firm_city, app.firm_country].filter(Boolean).join(', '),
    invoiceNumber: app.invoice_number || '-',
    annualFee: app.contract_price || 0,
    fundsIncluded: app.funds_included || 1,
    additionalFunds: extraFundCount(app),
    additionalFundPrice: app.additional_fund_price || 0,
    contractType: (app.contract_type === 'emerging' ? 'emerging' : 'standard'),
    maxUsers: app.max_users || 5,
    dueDate,
  };
}

// ── Encoding ─────────────────────────────────────────────────────────────────

/**
 * Base64-encode an HTML document. btoa alone throws on any character above
 * U+00FF, so the string is UTF-8 encoded first, firm names carry accents and
 * these documents use typographic quotes.
 */
export function htmlToBase64(html: string): string {
  const bytes = new TextEncoder().encode(html);
  let binary = '';
  bytes.forEach(b => { binary += String.fromCharCode(b); });
  return btoa(binary);
}

/** A Blob ready to upload to Supabase storage. */
export const htmlToBlob = (html: string) =>
  new Blob([html], { type: 'text/html;charset=utf-8' });
